import React, { useContext } from 'react';

import { WeightNumber } from '@dndbeyond/character-common-components/es';
import {
    Collapsible,
    CollapsibleHeaderContent,
    CollapsibleHeading,
    ItemName,
} from '@dndbeyond/character-components/es';
import {
    ApiAdapterPromise,
    ApiAdapterRequestConfig,
    ApiResponse,
    BaseItemDefinitionContract,
    CampaignUtils,
    CharacterTheme,
    Container,
    ContainerUtils,
    FeatureFlagManager,
    InventoryManager,
    CharacterCurrencyContract,
    Item,
    ItemUtils,
    Modifier,
    PartyInfo,
    RuleData,
    TypeValueLookup,
    CoinManager,
} from '@dndbeyond/character-rules-engine/es';

import CurrencyCollapsible from '../../../CharacterSheet/components/CurrencyCollapsible';
import { CoinManagerContext } from '../../managers/CoinManagerContext';
import { CurrencyErrorTypeEnum } from '../../containers/panes/CurrencyPane/CurrencyPaneConstants';
import { InventoryManagerContext } from '../../managers/InventoryManagerContext';
import { CustomItemCreator } from '../CustomItemCreator';
import EquipmentShop from '../EquipmentShop';
import ItemDetail from '../ItemDetail';
import { ThemeButtonWithMenu } from '../common/Button';
import { ItemSlotManager } from '../ItemSlotManager';

interface Props {
    currentContainer: Container;
    theme: CharacterTheme;
    loadAvailableItems?: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<Array<BaseItemDefinitionContract>>>;
    globalModifiers: Array<Modifier>;
    valueLookupByType: TypeValueLookup;
    onItemAdd?: (item: Item, amount: number, containerDefinitionKey: string) => void;
    onItemMove?: (item: Item, containerDefinitionKey: string) => void;
    onItemEquip?: (item: Item, uses: number) => void;
    ruleData: RuleData;
    proficiencyBonus: number;
    inventory: Array<Item>;
    containers: Array<Container>;
    shopOpenInitially?: boolean;
    isReadonly: boolean;
    partyInfo: PartyInfo | null;
    inventoryManager: InventoryManager;
    featureFlagManager: FeatureFlagManager;
    coinManager: CoinManager;
    handleCurrencyChangeError: (currencyName: string, errorType: CurrencyErrorTypeEnum) => void;
    handleCurrencyAdjust: (
        coin: Partial<CharacterCurrencyContract>,
        multiplier: 1 | -1,
        containerDefinitionKey: string,
    ) => void;
    handleAmountSet: (containerDefinitionKey: string, key: keyof CharacterCurrencyContract, amount: number) => void;
}

export const ContainerActionsComponent: React.FC<Props> = ({
    currentContainer,
    theme,
    loadAvailableItems,
    globalModifiers,
    valueLookupByType,
    ruleData,
    onItemAdd,
    onItemMove,
    onItemEquip,
    proficiencyBonus,
    inventory,
    containers,
    shopOpenInitially = false,
    isReadonly,
    partyInfo,
    inventoryManager,
    coinManager,
    handleCurrencyAdjust,
    handleAmountSet,
    handleCurrencyChangeError,
}) => {
    let showEquipmentShop = true;
    if (
        ContainerUtils.isShared(currentContainer) &&
        partyInfo &&
        CampaignUtils.isSharingStateInactive(CampaignUtils.getSharingState(partyInfo))
    ) {
        showEquipmentShop = false;
    }

    let headerNode: React.ReactNode = (
        <CollapsibleHeaderContent
            heading={<CollapsibleHeading>{`Contents (${inventory.length})`}</CollapsibleHeading>}
            callout={
                <div className="ct-equipment-manage-pane__callout">
                    <WeightNumber
                        number={ContainerUtils.getWeightInfo(currentContainer).total}
                        isDarkMode={theme.isDarkMode}
                    />
                </div>
            }
        />
    );

    return (
        <div className="ct-container-manager">
            {showEquipmentShop && (
                <Collapsible header="Add Items" initiallyCollapsed={!shopOpenInitially}>
                    <EquipmentShop
                        partyInfo={partyInfo}
                        containers={containers}
                        limitAddToCurrentContainer={currentContainer}
                        theme={theme}
                        loadItems={loadAvailableItems}
                        globalModifiers={globalModifiers}
                        valueLookupByType={valueLookupByType}
                        onItemAdd={onItemAdd}
                        ruleData={ruleData}
                        proficiencyBonus={proficiencyBonus}
                    />
                    <CustomItemCreator containers={[currentContainer]} />
                </Collapsible>
            )}
            {coinManager.canUseCointainers() && (
                <CurrencyCollapsible
                    heading={`${ContainerUtils.getName(currentContainer)} Coin`}
                    initiallyCollapsed={true}
                    isReadonly={isReadonly}
                    container={currentContainer}
                    handleCurrencyChangeError={handleCurrencyChangeError}
                    handleCurrencyAdjust={handleCurrencyAdjust}
                    handleAmountSet={handleAmountSet}
                />
            )}
            <Collapsible header={headerNode} initiallyCollapsed={shopOpenInitially}>
                <div className="ct-container-manager__inventory">
                    {inventory.map((item) => {
                        const canEquip = inventoryManager.canEquipUnequipItem(item);
                        return (
                            <Collapsible
                                key={ItemUtils.getUniqueKey(item)}
                                className="ct-container-manager__item"
                                layoutType={'minimal'}
                                header={
                                    <CollapsibleHeaderContent
                                        heading={
                                            <div className="ct-container-manager__item-header">
                                                <div className="ct-container-manager__item-header-action">
                                                    <ItemSlotManager
                                                        isUsed={!!ItemUtils.isEquipped(item)}

                                                        isReadonly={isReadonly}
                                                        canUse={canEquip}
                                                        onSet={(uses) => {
                                                            if (onItemEquip) {
                                                                onItemEquip(item, uses);
                                                            }
                                                        }}
                                                        theme={theme}
                                                        useTooltip={false}
                                                    />
                                                </div>
                                                <div className="ct-container-manager__item-header-name">
                                                    <ItemName item={item} theme={theme} />
                                                </div>
                                            </div>
                                        }
                                        callout={
                                            !isReadonly && partyInfo && inventoryManager.canMoveItem(item) ? (
                                                <ThemeButtonWithMenu
                                                    showSingleOption={true}
                                                    containerEl={
                                                        document.querySelector('.ct-sidebar__portal') as HTMLElement
                                                    }
                                                    groupedOptions={ContainerUtils.getGroupedOptions(
                                                        ItemUtils.getContainerDefinitionKey(item),
                                                        containers,
                                                        'Move To:',
                                                        CampaignUtils.getSharingState(partyInfo),
                                                    )}
                                                    buttonStyle="outline"
                                                    onSelect={(definitionKey) => {
                                                        if (onItemMove) {
                                                            onItemMove(item, definitionKey);
                                                        }
                                                    }}
                                                >
                                                    Move
                                                </ThemeButtonWithMenu>
                                            ) : null
                                        }
                                    />
                                }
                            >
                                <ItemDetail
                                    theme={theme}
                                    item={item}
                                    ruleData={ruleData}
                                    showCustomize={false}
                                    showImage={false}
                                    proficiencyBonus={proficiencyBonus}
                                />
                            </Collapsible>
                        );
                    })}
                    {ContainerUtils.getItemMappingIds(currentContainer).length === 0 && (
                        <div className="ct-container-manager__empty">
                            There are no items in your {ContainerUtils.getName(currentContainer)}
                        </div>
                    )}
                </div>
            </Collapsible>
        </div>
    );
};

export const ContainerActions = (props) => {
    const { inventoryManager } = useContext(InventoryManagerContext);
    const { coinManager } = useContext(CoinManagerContext);
    return <ContainerActionsComponent inventoryManager={inventoryManager} coinManager={coinManager} {...props} />;
};

export default ContainerActions;
